import { Injectable } from '@angular/core';
import { FlamingoAdapter } from '@mode-switch/adapter-flamingo';
import { DatasetResult, FlamingoTypes, ModeModel } from '@mode/shared/contract-common';
import { makeFilteredRefId } from '@mode/shared/util-js';
import { DomainQueryBuilder } from '@mode/shared/util-switch';
import { FlamingoActions, FlamingoFacade } from '@mode/switch-views/contract';
import { Store } from '@ngrx/store';
import { ModeExportPlan } from 'libs/shared/contract-common/src/lib/flamingo.types';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { FlamingoRoot } from './flamingo/flamingo.reducer';
import {
  createBinSizeToFieldMapSelector,
  createColumnsSelector,
  createConditionalFormattingSelector,
  createCountSelector,
  createDataProfilingSelector,
  createDomainRecordsSelector,
  createErrorsSelector,
  createExportSelector,
  createFailedSelector,
  createInitSelector,
  createLoadingSelector,
  createPageLoadingSelector,
  createPlanIdSelector,
  createPlanSelector,
  createReadySelector,
  createRecordsSelector,
  createResultSelector,
  createStateSelector,
  getCompletedPlanRefs,
  getPlanFromRefId,
} from './flamingo/flamingo.selectors';

@Injectable()
export class FlamingoFacadeService implements FlamingoFacade {
  private readonly _adapter = new FlamingoAdapter();

  constructor(private store: Store<FlamingoRoot>) {
    // init observables
    this.completedPlanRefs$ = this.store.select(getCompletedPlanRefs);
  }

  /**
   * Observables
   */
  completedPlanRefs$: Observable<FlamingoTypes.FlamingoPlanRef>;

  /**
   * Observable creators
   */

  createPlanIdFromRefId(refId: string) {
    return this.store.select(createPlanIdSelector(refId));
  }

  createPlanFromPlanId(planId: string) {
    return this.store.select(createPlanSelector(planId));
  }

  createStateFromPlanId(planId: string) {
    return this.store.select(createStateSelector(planId));
  }

  createReadyFromPlanId(planId: string) {
    return this.store.select(createReadySelector(planId));
  }

  createErrorFromPlanId(planId: string) {
    return this.store.select(createErrorsSelector(planId));
  }

  createExportFromPlanId(planId: string) {
    return this.store.select(createExportSelector(planId));
  }

  createRecordsFromPlanId(planId: string) {
    return this.store.select(createRecordsSelector(planId));
  }

  createResultFromPlanId(planId: string) {
    return this.store.select(createResultSelector(planId));
  }

  createCountFromPlanId(planId: string) {
    return this.store.select(createCountSelector(planId));
  }

  createColumnsByRefId(refId: string) {
    return this.store.select(createColumnsSelector(refId));
  }

  createDataProfilingFromVizToken(vizToken: string) {
    return this.store.select(createDataProfilingSelector(vizToken));
  }

  createBinSizeToFieldMapFromVizToken(vizToken: string) {
    return this.store.select(createBinSizeToFieldMapSelector(vizToken));
  }

  createConditionalFormattingFromVizToken(vizToken: string) {
    return this.store.select(createConditionalFormattingSelector(vizToken));
  }

  createLoadingByPlanId(planId: string) {
    return this.store.select(createLoadingSelector(planId));
  }

  createInitByPlanId(planId: string) {
    return this.store.select(createInitSelector(planId));
  }

  createPageLoadingByPlanId(planId: string) {
    return this.store.select(createPageLoadingSelector(planId));
  }

  createFailedByPlanId(planId: string) {
    return this.store.select(createFailedSelector(planId));
  }

  createDomainRecordsSelector(planId: string) {
    return this.store.select(createDomainRecordsSelector(planId));
  }

  getPlanFromRefId(refId: string) {
    return this.store.select(getPlanFromRefId(refId));
  }

  /**
   * Action dispatchers
   */

  /**
   * Execute flamingo page plan.
   */
  executePagePlan(
    plan: FlamingoTypes.ModeSelectPlan,
    refId: string,
    queryRunToken: string,
    allowRefresh: boolean
  ): void {
    this.store.dispatch(FlamingoActions.executePagePlan({ plan, refId, queryRunToken, allowRefresh }));
  }

  getContent(refId: string): Observable<FlamingoTypes.RecordsResult['content']> {
    return this.createPlanIdFromRefId(refId).pipe(
      distinctUntilChanged(),
      switchMap((planId) => this.createRecordsFromPlanId(planId)),
      filter((res) => res != null),
      map((records: any) => records['content'])
    );
  }

  performDomainQuery(datasetResult: DatasetResult, fields: FlamingoTypes.ModeFields, model: ModeModel): string {
    this.store.dispatch(
      FlamingoActions.executePagePlan({
        plan: DomainQueryBuilder.buildDomainQuery(this._adapter, model, datasetResult.queryToken, fields),
        refId: makeFilteredRefId(datasetResult.queryToken),
        queryRunToken: datasetResult.queryRunToken,
        allowRefresh: true,
      })
    );

    return datasetResult.queryToken;
  }

  exportCsv(refId: string, plan: ModeExportPlan, queryRunToken: string) {
    this.store.dispatch(
      FlamingoActions.executeExportPlan({
        queryRunToken,
        plan,
        refId,
        allowRefresh: true,
      })
    );
  }
}
